import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PageTitle } from '@/components/molecules/PageTitle';
import { Typography } from '@/components/atoms/Typography';
import { TbArrowsRight, TbCheck, TbUser, TbX } from 'react-icons/tb';
import { useApproveClaimWithTokenMutation, useRejectClaimWithTokenMutation } from '@/api/hooks/useClaim';
import toast from 'react-hot-toast';
import { Spinner } from '@/components/atoms/Spinner';
import clsx from 'clsx';

export const ClaimApproveRejectPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  const restaurantName = params.get('restaurantName');
  const newEmail = params.get('newEmail');
  const ownerEmail = params.get('ownerEmail');

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const confirmationPhrase = "je suis d'accord pour ceder la propriete";

  const normalizeText = (text: string) =>
    text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const { isLoading: isApprovingClaim, mutate: approveClaim } = useApproveClaimWithTokenMutation({
    onSuccess: () => {
      navigate(`/claim?info=claimApproved&restaurantName=${restaurantName}`);
      toast.success('Transfert de propriété approuvé', {
        position: 'bottom-right'
      });
    }
  });

  const { isLoading: isRejectingClaim, mutate: rejectClaim } = useRejectClaimWithTokenMutation({
    onSuccess: () => {
      navigate(`/claim?info=claimRejected&restaurantName=${restaurantName}`);
      toast.success('Transfert de propriété rejeté', {
        position: 'bottom-right'
      });
    }
  });

  const handleApproveClick = () => {
    setShowConfirmation(true);
  };

  const handleFinalApprove = () => {
    if (token) {
      approveClaim(token);
    } else {
      toast.error('Token manquant', {
        position: 'bottom-right'
      });
    }
  };

  const handleReject = () => {
    if (token) {
      rejectClaim(token);
    } else {
      toast.error('Token manquant', {
        position: 'bottom-right'
      });
    }
  };

  const isLoading = isApprovingClaim || isRejectingClaim;
  const isConfirmationValid = normalizeText(confirmationText) === confirmationPhrase;

  return (
    <div className="h-screen w-full mx-auto bg-yellow-50">
      <div className="max-w-6xl mx-auto">
        <PageTitle firstTitle="Processus de transfert de propriétaire" />
      </div>

      <div className="flex items-center md:mx-auto m-5 text-center mt-10 justify-center flex-col bg-yellow-200 w-fit py-5 px-10 rounded-lg">
        <Typography variant="h2">
          Que souhaitez-vous faire pour le choix de propriétaire pour <b>{restaurantName}</b> ?
        </Typography>
      </div>

      <div className="flex md:flex-row flex-col items-center justify-center gap-5 mt-10">
        <button
          onClick={handleApproveClick}
          disabled={isLoading}
          className="border rounded-xl w-fit p-3 bg-emerald-50 hover:bg-emerald-200 duration-200 text-black hover:text-emerald-700 ease-in-out"
        >
          <div className="bg-emerald-400 p-5 rounded-xl items-center flex flex-col w-fit">
            <Typography color="white" variant="h2" weight="bold">
              Transfert du propriétaire
            </Typography>
            <div className="flex md:flex-row flex-col mt-2 text-white items-center">
              <div>
                <span className="flex gap-1 items-center font-semibold">
                  <TbUser size={23} />
                  {ownerEmail}
                </span>
                <p className="text-xs">Propriétaire actuel</p>
              </div>

              <TbArrowsRight className="my-2 mx-0 md:my-0 md:mx-5 rotate-90 md:rotate-0" size={24} />

              <div>
                <span className="flex gap-1 items-center font-semibold">
                  <TbUser size={23} />
                  {newEmail}
                </span>
                <p className="text-xs">Propriétaire futur</p>
              </div>
            </div>
          </div>
          <div className="flex mt-3 items-center gap-2 justify-center">
            {isLoading && <Spinner />}
            <p className="font-semibold">Accepter le transfert</p>
          </div>
        </button>

        <button
          onClick={handleReject}
          disabled={isLoading}
          className="border rounded-xl w-fit p-3 bg-red-50 hover:bg-red-200 duration-200 text-black hover:text-red-700 ease-in-out"
        >
          <div className="bg-red-400 p-5 rounded-xl items-center flex flex-col w-fit">
            <Typography color="white" variant="h2" weight="bold">
              Maintient du propriétaire
            </Typography>

            <span className="flex gap-1 items-center text-white mt-2 font-semibold">
              <TbUser size={23} />
              {ownerEmail}
            </span>
            <p className="text-xs text-white">Propriétaire actuel</p>
          </div>
          <div className="flex mt-3 items-center gap-2 justify-center">
            {isLoading && <Spinner />}
            <p className="font-semibold">Refuser le transfert</p>
          </div>
        </button>
      </div>

      {showConfirmation && (
        <div
          className={clsx(!isConfirmationValid || isLoading ? 'bg-white' : 'bg-emerald-50', 'flex flex-col items-center justify-center mt-24 p-5   border-y')}
        >
          <Typography variant="h2">Confirmez votre accord</Typography>
          <p className="text-sm text-gray-600 text-center">Tapez la phrase suivante pour valider le transfert :</p>
          <p className="font-bold text-center text-red-600">"je suis d'accord pour céder la propriété"</p>
          <div className="flex items-center gap-2 mt-3">
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Tapez la phrase exacte ici..."
              className={clsx(
                !isConfirmationValid || isLoading ? 'focus:border-gray-600' : 'focus:border-emerald-400',
                'border rounded p-2 w-80 text-center focus:outline-none focus:ring-0 '
              )}
            />

            <button
              onClick={handleFinalApprove}
              disabled={!isConfirmationValid || isLoading}
              className={`border rounded-md px-3 py-1 flex gap-2 ${
                !isConfirmationValid || isLoading
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-emerald-400 duration-200 ease-in-out  hover:bg-emerald-500 text-white'
              }`}
            >
              Finaliser le transfert
              {!isConfirmationValid || (isLoading && <TbCheck size={24} />)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
